import { z } from 'zod';
import { currencyCodeSchema, idSchema } from './common.js';
import { ledgerDateSchema } from './financial-accounting.js';
import { fiscalFxProofSchema } from './fiscal-fx.js';
import { addDecimal, compareDecimal, subtractDecimal } from '../utils/decimal.js';
const MONEY_SCALE = 4;
const money = z.string().regex(/^(?:0|[1-9]\d{0,13})\.\d{4}$/u);
const signedMoney = z.string().regex(/^-?(?:0|[1-9]\d{0,13})\.\d{4}$/u);
const positiveMoney = money.refine((value) => money.safeParse(value).success && compareDecimal(value, '0') > 0);
const ratePattern = /^(?:0|[1-9]\d{0,13})\.\d{10}$/u;
const rate = z
    .string()
    .regex(ratePattern)
    .refine((value) => ratePattern.test(value) && compareDecimal(value, '0') > 0);
const foreignCurrency = currencyCodeSchema.refine((value) => value !== 'EUR');
export const foreignAdvanceRangeSchema = z.object({ fromGross: money, toGross: money }).strict();
// These budgets retain captured arithmetic; native payment and ownership evidence remains separate.
export const foreignAdvanceBudgetEffectSchema = z
    .object({
    nominal: z
        .object({
        gross: positiveMoney,
        legalNet: money,
        legalTax: money,
        deductibleTax: money,
    })
        .strict(),
    postedHome: z
        .object({ moneyAmount: money, carryingAmount: signedMoney, taxAmount: money })
        .strict(),
    fiscalHome: z.object({ legalNet: money, legalTax: money, deductibleTax: money }).strict(),
})
    .strict()
    .superRefine((value, context) => {
    const amounts = [
        ...Object.values(value.nominal),
        ...Object.values(value.postedHome),
        ...Object.values(value.fiscalHome),
    ];
    if (amounts.some((amount) => !signedMoney.safeParse(amount).success))
        return;
    if (compareDecimal(addDecimal(value.nominal.legalNet, value.nominal.legalTax, MONEY_SCALE), value.nominal.gross) !== 0 ||
        compareDecimal(value.nominal.deductibleTax, value.nominal.legalTax) > 0 ||
        compareDecimal(addDecimal(value.postedHome.carryingAmount, value.postedHome.taxAmount, MONEY_SCALE), value.postedHome.moneyAmount) !== 0 ||
        value.postedHome.taxAmount !== value.fiscalHome.deductibleTax ||
        compareDecimal(value.fiscalHome.deductibleTax, value.fiscalHome.legalTax) > 0)
        context.addIssue({ code: 'custom', message: 'Invalid foreign advance budget' });
});
export const foreignAdvanceReceiptBucketSchema = z
    .object({
    sourceBucketIndex: z.number().int().nonnegative().max(10000),
    rate: z.string().regex(/^\d{1,3}(?:\.\d{1,4})?$/u),
    nominal: foreignAdvanceBudgetEffectSchema.innerType().shape.nominal,
    postedHome: foreignAdvanceBudgetEffectSchema.innerType().shape.postedHome,
    fiscalHome: foreignAdvanceBudgetEffectSchema.innerType().shape.fiscalHome,
    taxAccountId: idSchema.nullable(),
    slices: z.array(foreignAdvanceRangeSchema).min(1).max(1000),
})
    .strict()
    .superRefine((value, context) => {
    const valid = foreignAdvanceBudgetEffectSchema.safeParse({
        nominal: value.nominal,
        postedHome: value.postedHome,
        fiscalHome: value.fiscalHome,
    });
    if (!valid.success) {
        context.addIssue({ code: 'custom', message: 'Invalid foreign advance budget' });
        return;
    }
    if (value.slices.some((slice) => !foreignAdvanceRangeSchema.safeParse(slice).success))
        return;
    let total = '0.0000';
    let previous = '0.0000';
    for (const slice of value.slices) {
        if (compareDecimal(slice.fromGross, previous) < 0 ||
            compareDecimal(slice.toGross, slice.fromGross) <= 0)
            context.addIssue({ code: 'custom', message: 'Invalid foreign advance ownership' });
        total = addDecimal(total, subtractDecimal(slice.toGross, slice.fromGross, MONEY_SCALE), MONEY_SCALE);
        previous = slice.toGross;
    }
    if (compareDecimal(total, value.nominal.gross) !== 0 ||
        (compareDecimal(value.postedHome.taxAmount, '0') === 0) !== (value.taxAccountId === null))
        context.addIssue({ code: 'custom', message: 'Invalid foreign advance ownership' });
});
export const foreignAdvanceAttributionSelectionSchema = z
    .object({
    schemaVersion: z.literal(2),
    currency: foreignCurrency,
    moneyAmount: positiveMoney,
    fiscalRateId: idSchema,
    fiscalRateVersion: z.number().int().positive(),
})
    .strict();
export const foreignAdvanceRefundSelectionSchema = z
    .object({ schemaVersion: z.literal(2), currency: foreignCurrency, moneyAmount: positiveMoney })
    .strict();
const reviewedMoney = z
    .object({
    currency: currencyCodeSchema,
    amount: positiveMoney,
    homeAmount: money,
    paymentBookRate: rate,
    paymentBookRateDate: ledgerDateSchema,
    nominalSettlementRate: rate,
})
    .strict();
export const foreignAdvanceAttributionReviewSchema = z
    .object({
    schemaVersion: z.literal(2),
    kind: z.enum(['RECEIPT', 'REFUND']),
    previewHash: z.string().regex(/^[a-f0-9]{64}$/u),
    currency: foreignCurrency,
    sourceNumber: z.string().min(1).max(200),
    sourceVersion: z.number().int().positive(),
    method: z.enum(['SINGLE_BUCKET', 'EXPLICIT_SOURCE', 'REVIEWED_PRORATA']),
    reason: z.string().trim().min(10).max(1000),
    paymentDate: ledgerDateSchema,
    refundDate: ledgerDateSchema.optional(),
    effectiveDate: ledgerDateSchema,
    nominalGross: positiveMoney,
    money: reviewedMoney,
    refundMoney: reviewedMoney.optional(),
    fiscalFx: fiscalFxProofSchema,
    buckets: z.array(foreignAdvanceReceiptBucketSchema).min(1).max(100),
})
    .strict()
    .superRefine((value, context) => {
    const invalid = (path, message) => context.addIssue({ code: 'custom', path: [path], message });
    if ((value.kind === 'REFUND') !== (value.refundDate !== undefined) ||
        (value.refundMoney !== undefined && value.kind !== 'REFUND') ||
        (value.refundDate !== undefined &&
            (value.refundDate < value.paymentDate || value.refundDate > value.effectiveDate)))
        invalid('refundDate', 'Invalid foreign advance refund date');
    if (value.paymentDate > value.effectiveDate)
        invalid('effectiveDate', 'Effective date precedes actual payment');
    if (!positiveMoney.safeParse(value.nominalGross).success ||
        !reviewedMoney.safeParse(value.money).success ||
        !fiscalFxProofSchema.safeParse(value.fiscalFx).success ||
        value.buckets.some((bucket) => !foreignAdvanceReceiptBucketSchema.safeParse(bucket).success))
        return;
    if (value.fiscalFx.rate.sourceCurrency !== value.currency ||
        value.fiscalFx.taxDate !== value.paymentDate ||
        value.fiscalFx.rate.validFrom > value.paymentDate ||
        value.fiscalFx.rate.validTo < value.paymentDate)
        invalid('fiscalFx', 'Fiscal quote does not cover the original payment');
    if (value.money.currency === value.currency
        ? compareDecimal(value.money.amount, value.nominalGross) !== 0
        : value.money.currency !== 'EUR' ||
            compareDecimal(value.money.amount, value.money.homeAmount) !== 0 ||
            compareDecimal(value.money.paymentBookRate, '1') !== 0)
        invalid('money', 'Actual money currency and amounts do not reconcile');
    if (value.refundMoney &&
        (value.refundMoney.currency === value.currency
            ? compareDecimal(value.refundMoney.amount, value.nominalGross) !== 0
            : value.refundMoney.currency !== 'EUR' ||
                compareDecimal(value.refundMoney.amount, value.refundMoney.homeAmount) !== 0 ||
                compareDecimal(value.refundMoney.paymentBookRate, '1') !== 0))
        invalid('refundMoney', 'Actual refund currency and amounts do not reconcile');
    const seen = new Set();
    let nominalGross = '0.0000';
    let homeMoney = '0.0000';
    for (const bucket of value.buckets) {
        if (seen.has(bucket.sourceBucketIndex))
            invalid('buckets', 'Source bucket indices must be unique');
        seen.add(bucket.sourceBucketIndex);
        nominalGross = addDecimal(nominalGross, bucket.nominal.gross, MONEY_SCALE);
        homeMoney = addDecimal(homeMoney, bucket.postedHome.moneyAmount, MONEY_SCALE);
    }
    if (compareDecimal(nominalGross, value.nominalGross) !== 0 ||
        compareDecimal(homeMoney, value.money.homeAmount) !== 0)
        invalid('buckets', 'Reviewed nominal and posted money totals do not reconcile');
    // Native evidence validates book rates and slice budgets; partial refunds retain the full quote.
});
//# sourceMappingURL=foreign-advance-attribution.js.map